import { EChartOption } from 'echarts'
import { FC, useMemo, useState } from 'react'
import { EChart } from 'src/charts/EChart'
import { COLORS } from 'src/constants/colors'
import { ReactComponent as LoudspeakerSvg } from 'src/assets/svg/loudspeaker.svg'
import { Skeleton } from 'src/components/Skeleton'
import { numberWithCommas } from 'src/utils/numberFormat'
import { CommentsChartMockData } from 'src/pages/ContentAnalysisDetail/mockData/commentsChartMockData'
import styles from './commentsDonutChart.module.scss'

// Real dimensions from commentsDonutChart.module.scss: .percentage is 32px/38px
// line-height, .count 14px, .sentimentLabel 16px and .totalLabel 13px. The
// donut sits in a FIXED 180x180 .chartWrapper and is left alone; only the
// readouts are skeletoned — they otherwise show a confident "0.0%" / "Mostly
// Positive" / "Out of 0" before any comments have arrived.
const PERCENTAGE_HEIGHT = 38
const PERCENTAGE_WIDTH = 96
const COUNT_HEIGHT = 16
const COUNT_WIDTH = 62
const SENTIMENT_LABEL_HEIGHT = 19
const SENTIMENT_LABEL_WIDTH = 118
// 13px text (~15px line box) + the 4px margin-top .totalLabel carries, which the
// skeleton block absorbs into its own height instead.
const TOTAL_LABEL_HEIGHT = 19
const TOTAL_LABEL_WIDTH = 92

interface CommentsDonutChartProps {
  data: CommentsChartMockData
  isLoading?: boolean
}

interface HoveredData {
  count: number
  percentage: number
}

export const CommentsDonutChart: FC<CommentsDonutChartProps> = ({
  data,
  isLoading = false,
}) => {
  const { sentimentBreakdown, totalComments } = data
  const [hoveredData, setHoveredData] = useState<HoveredData | null>(null)

  const dominantSentiment = useMemo(() => {
    const { negativePercentage, neutralPercentage, positivePercentage } =
      sentimentBreakdown
    if (
      positivePercentage >= neutralPercentage &&
      positivePercentage >= negativePercentage
    ) {
      return 'Mostly Positive'
    }
    if (
      neutralPercentage >= positivePercentage &&
      neutralPercentage >= negativePercentage
    ) {
      return 'Mostly Neutral'
    }
    return 'Mostly Negative'
  }, [sentimentBreakdown])

  const dominantCount = useMemo(() => {
    const { negative, neutral, positive } = sentimentBreakdown
    if (dominantSentiment === 'Mostly Positive') return positive
    if (dominantSentiment === 'Mostly Neutral') return neutral
    return negative
  }, [dominantSentiment, sentimentBreakdown])

  const dominantPercentage = useMemo(() => {
    const { negativePercentage, neutralPercentage, positivePercentage } =
      sentimentBreakdown
    if (dominantSentiment === 'Mostly Positive') return positivePercentage
    if (dominantSentiment === 'Mostly Neutral') return neutralPercentage
    return negativePercentage
  }, [dominantSentiment, sentimentBreakdown])

  const displayPercentage = hoveredData?.percentage ?? dominantPercentage
  const displayCount = hoveredData?.count ?? dominantCount

  const options: EChartOption<EChartOption.SeriesPie> | undefined =
    useMemo(() => {
      const pieData = [
        {
          name: 'Positive',
          value: sentimentBreakdown.positive,
        },
        {
          name: 'Neutral',
          value: sentimentBreakdown.neutral,
        },
        {
          name: 'Negative',
          value: sentimentBreakdown.negative,
        },
      ]

      const chartOptions: EChartOption<EChartOption.SeriesPie> = {
        color: [COLORS.malibu, COLORS.grey200, COLORS.burningOrange],
        series: [
          {
            avoidLabelOverlap: true,
            data: pieData,
            emphasis: {
              // @ts-ignore-next-line
              scale: false,
            },
            itemStyle: {
              borderColor: COLORS.white,
              borderWidth: 4,
            },
            label: {
              show: false,
            },
            labelLine: {
              show: false,
            },
            radius: ['72%', '92%'],
            type: 'pie',
          },
        ],
        tooltip: {
          formatter: '{b}: {c} ({d}%)',
          trigger: 'item',
        },
      }

      return chartOptions
    }, [sentimentBreakdown])

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartWrapper}>
        <EChart
          onMouseOut={() => setHoveredData(null)}
          onMouseOver={(event) => {
            if (event.percent !== undefined && event.value !== undefined) {
              setHoveredData({
                count: event.value as number,
                percentage: event.percent,
              })
            }
          }}
          options={options}
        />
        <div className={styles.centerInfo}>
          {isLoading ? (
            <>
              <Skeleton height={PERCENTAGE_HEIGHT} width={PERCENTAGE_WIDTH} />
              <Skeleton height={COUNT_HEIGHT} width={COUNT_WIDTH} />
            </>
          ) : (
            <>
              <span className={styles.percentage}>
                {displayPercentage.toFixed(1)}%
              </span>
              <span className={styles.count}>
                {numberWithCommas(displayCount)}
                <LoudspeakerSvg className={styles.loudspeakerIcon} />
              </span>
            </>
          )}
        </div>
      </div>
      <div className={styles.bottomInfo}>
        {isLoading ? (
          <>
            <Skeleton
              height={SENTIMENT_LABEL_HEIGHT}
              width={SENTIMENT_LABEL_WIDTH}
            />
            <Skeleton height={TOTAL_LABEL_HEIGHT} width={TOTAL_LABEL_WIDTH} />
          </>
        ) : (
          <>
            <span className={styles.sentimentLabel}>{dominantSentiment}</span>
            <span className={styles.totalLabel}>
              Out of {numberWithCommas(totalComments)}
              <LoudspeakerSvg className={styles.loudspeakerIconSmall} />
            </span>
          </>
        )}
      </div>
    </div>
  )
}
